import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { EntityTypeEnum, LabelPreviewWithoutTypeOutDTO } from '@api/ringil-api.v2.tsx';
import { ApiContext } from '@api/api.tsx';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilSpinner from '@components/ringil3/RingilSpinner/RingilSpinner.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import EditButton from '@components/ringil3/Buttons/EditButton.tsx';
import InputTextWithSuffix from '@components/ringil3/Input/InputTextWithSuffix/InputTextWithSuffix.tsx';
import { RowWithGap } from '@components/styles.tsx';
import SvgLabel from '@components/ringil3/icons/Label.tsx';
import SvgTagPlus from '@components/ringil3/icons/TagPlus.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import { ClickableCursor, HorizontalLine } from '@components/ringil3/styles.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import { useDebounce } from 'use-debounce';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import RingilLabelForm from '@components/RingilComponents/LabelForm/RingilLabelForm.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import clsx from 'clsx';
import { toTestIdSlug } from '@utils/testUtils';

interface ComponentProps {
  secured?: SecuredConfig;
  objectType: EntityTypeEnum;
  objectId?: string;
  resolveLabelsForType?: EntityTypeEnum;
  disabled?: boolean;
  onChange?: (labels: LabelPreviewWithoutTypeOutDTO[]) => void;
  value?: LabelPreviewWithoutTypeOutDTO[];
  className?: string;
  wrapperWidth?: string;
  dataTestId?: string;
}

const LabelsTag: React.FC<ComponentProps> = ({ secured, objectType, objectId, className, resolveLabelsForType, disabled, onChange, value, wrapperWidth, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const { getAllLabelsForCompanyEntityType, cuGetLabelsForEntity, cuSetLabelsForEntity, archiveLabel } = useContext(ApiContext);
  // When secured supplies an editability rule it is authoritative; otherwise fall back to the disabled prop.
  const { editable } = useSecuredDecoration(secured);
  const effectiveDisabled = editable === null ? !!disabled : !editable;
  const [labels, setLabels] = useState<LabelPreviewWithoutTypeOutDTO[]>(value || []);
  const [labelsForEntity, setLabelsForEntity] = useState<LabelPreviewWithoutTypeOutDTO[]>(value || []);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 700);
  const [apiReqCount, setApiReqCount] = useState(0);
  const [showDeleteLabel, setShowDeleteLabel] = useState(false);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [addingLabel, setAddingLabel] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isNotNullOrUndefined(value)) {
      setLabelsForEntity(value);
    }
  }, [value]);

  const load = useCallback(() => {
    setIsLoadingLabels(true);
    setApiReqCount(c => c + 1);

    // Batch the API calls to reduce flickering
    const loadAllLabels = new Promise<void>((resolve) => {
      getAllLabelsForCompanyEntityType(resolveLabelsForType, searchText, d => {
        setLabels(d);
        resolve();
      });
    });

    const loadEntityLabels = objectId != null ? new Promise<void>((resolve) => {
      cuGetLabelsForEntity(objectType, objectId, d => {
        setLabelsForEntity(d);
        resolve();
      });
    }) : Promise.resolve();

    Promise.all([loadAllLabels, loadEntityLabels]).finally(() => {
      setApiReqCount(c => c - 1);
      setIsLoadingLabels(false);
    });
  }, [getAllLabelsForCompanyEntityType, resolveLabelsForType, searchText, objectId, objectType, cuGetLabelsForEntity]);

  useEffect(() => {
    load();
  }, [debouncedSearchText, load]);

  const deleteLabel = useCallback((labelId: string) => {
    if (isNotNullOrUndefined(objectId)) {
      // Optimistic update - immediately update UI
      const newLabels = labelsForEntity.filter(t => t.labelId !== labelId);
      setLabelsForEntity(newLabels);

      setApiReqCount(c => c + 1);
      cuSetLabelsForEntity(
        objectType,
        objectId,
        {
          labelIds: newLabels.map(t => t.labelId),
        },
        () => {
          setApiReqCount(c => c - 1);
          // Only reload if we need fresh data
          if (searchText) {
            load();
          }
          // Notify the parent of the persisted change so an adjacent view (e.g. an audit log) can refresh.
          onChange?.(newLabels);
        },
        null, // hashAuthentication
        {
          // callbacks object for error handling
          onError: (_error, showStandardError) => {
            setApiReqCount(c => c - 1);
            setLabelsForEntity(labelsForEntity); // Revert optimistic update
            showStandardError();
          }
        }
      );
    } else {
      const newLabels = labelsForEntity.filter(t => t.labelId !== labelId);
      setLabelsForEntity(newLabels);
      onChange?.(newLabels);
    }
  }, [objectId, objectType, cuSetLabelsForEntity, labelsForEntity, onChange, searchText, load]);

  const addLabel = useCallback((labelId: string) => {
    const labelToAdd = labels.find(t => t.labelId === labelId);
    if (!labelToAdd) return;

    if (objectId == null) {
      const newLabels = [...labelsForEntity, labelToAdd];
      setLabelsForEntity(newLabels);
      onChange?.(newLabels);
    } else {
      // Optimistic update
      const newLabels = [...labelsForEntity, labelToAdd];
      setLabelsForEntity(newLabels);

      setApiReqCount(c => c + 1);
      cuSetLabelsForEntity(
        objectType,
        objectId,
        { labelIds: newLabels.map(t => t.labelId) },
        () => {
          setApiReqCount(c => c - 1);
          // Only reload if we need fresh data
          if (searchText) {
            load();
          }
          // Notify the parent of the persisted change so an adjacent view (e.g. an audit log) can refresh.
          onChange?.(newLabels);
        },
        null, // hashAuthentication
        {
          // callbacks object for error handling
          onError: (_error, showStandardError) => {
            setApiReqCount(c => c - 1);
            setLabelsForEntity(labelsForEntity); // Revert optimistic update
            showStandardError();
          }
        }
      );
    }
  }, [objectId, objectType, cuSetLabelsForEntity, labelsForEntity, onChange, labels, searchText, load]);

  // Convert existingLabels from useMemo to useCallback since it's called as a function
  const existingLabels = useCallback(() => {
    return (
      <RowWithGap>
        <div className="flex flex-wrap gap-1">
          {labelsForEntity?.map((label) => {
            return (
              <COBadge
                key={label.labelId} // Use labelId instead of index for better React reconciliation
                variant={'filled'}
                type={'custom'}
                backgroundColor={label.labelColor}
                icon={effectiveDisabled ? null : <SvgDelete className='text-base-secondary' />}
                onIconClick={e => {
                  e.preventDefault();
                  if (effectiveDisabled) return;
                  deleteLabel(label.labelId);
                }}
              >
                <div className={'text-white'}>{label.labelValue}</div>
              </COBadge>
            );
          })}
        </div>
        <RingilSpinner isSpinning={apiReqCount > 0} />
      </RowWithGap>
    );
  }, [labelsForEntity, effectiveDisabled, deleteLabel, apiReqCount]);

  const loadLabelsHandler = () => {
    load();
  };

  const archiveLabelHandler = (labelId: string) => {
    archiveLabel(
      labelId,
      () => {
        loadLabelsHandler();
      },
      null,
    );
  };

  const pane = useMemo(() => (
    <div className="w-full flex flex-col gap-4">
      {!showDeleteLabel ? (
        <>
          <Heading
            headingVariant={'h4'}
            title={tr(`LabelsTag.labels`, `Labels`)}
            buttons={[
              <EditButton
                onClick={() => {
                  setShowDeleteLabel(true);
                }}
              />,
            ]}
          ></Heading>
          <InputTextWithSuffix
            suffix={
              <>
                <img src={'/images/icons/ringil3/inputsearch.svg'} />
              </>
            }
            value={searchText}
            onChange={v => setSearchText(v)}
          />
          {!isLoadingLabels && (
            <div className="flex flex-wrap gap-1">
              {labels?.map((label) => {
                const optionTestId = dataTestId ? `${dataTestId}-${toTestIdSlug(label.labelValue)}` : undefined;
                return (
                  <COBadge
                    key={label.labelId} // Use labelId instead of index
                    variant={'filled'}
                    type={'custom'}
                    color={'white'}
                    backgroundColor={label.labelColor}
                    dataTestId={optionTestId}
                    onClick={() => {
                      addLabel(label.labelId);
                    }}
                  >
                    {label.labelValue}
                  </COBadge>
                );
              })}
            </div>
          )}
          {isLoadingLabels && (
            <div className="flex justify-center py-4">
              <RingilSpinner isSpinning={true} />
            </div>
          )}
          <>
            {!addingLabel && (
              <ClickableCursor onClick={() => setAddingLabel(true)}>
                <LinkWithIcon icon={<SvgTagPlus />} label={tr(`LabelsTag.addLabel`, `Add label`)} />
              </ClickableCursor>
            )}
            {addingLabel && (
              <>
                <HorizontalLine variant={'tertiary'} />
                <Heading headingVariant={'h4'} title={tr(`LabelsTag.addLabelTitle`, `Add label`)} buttons={[]}></Heading>
                <RingilLabelForm
                  entityType={resolveLabelsForType}
                  onCreated={() => {
                    setAddingLabel(false);
                    load();
                  }}
                />
              </>
            )}
          </>
        </>
      ) : (
        <>
          <Heading headingVariant={'h4'} title={tr(`LabelsTag.removeLabel`, `Remove label`)} buttons={[]}></Heading>
          <div className="flex flex-wrap gap-1">
            {labels?.map((label, index) => {
              const optionTestId = dataTestId ? `${dataTestId}-${toTestIdSlug(label.labelValue)}` : undefined;
              return (
                <COBadge
                  key={index}
                  variant={'outlined'}
                  type={'custom'}
                  backgroundColor={label.labelColor}
                  dataTestId={optionTestId}
                  onClick={e => {
                    e.preventDefault();
                    archiveLabelHandler(label.labelId);
                  }}
                >
                  {label.labelValue}
                  <SvgDelete />
                </COBadge>
              );
            })}
          </div>
          <div>
            <RingilButton
              label={tr(`LabelsTag.done`, `Done`)}
              onClick={() => {
                setShowDeleteLabel(false);
              }}
              mode={'filled'}
            />
          </div>
        </>
      )}
    </div>
  ), [showDeleteLabel, searchText, isLoadingLabels, labels, addLabel, addingLabel, load, resolveLabelsForType, tr, archiveLabelHandler, dataTestId]);

  if (isNullOrUndefined(labelsForEntity)) return <></>;

  return (
    <div ref={containerRef}>
      <ButtonTag secured={secured}
        isLabelsButtonTag
        icon={<SvgLabel />}
        label={labelsForEntity?.length > 0 ? existingLabels() : null}
        onClose={() => {
          setShowDeleteLabel(false);
          setAddingLabel(false);
        }}
        actionPane={pane}
        hideButtons={true}
        autoClose={true}
        value={labelsForEntity?.length > 0 ? labelsForEntity?.map(t => t.labelValue).join(', ') : null}
        readonly={disabled}
        popupWidth={wrapperWidth || "600px"}
        className={clsx(className, 'flex items-center', labelsForEntity?.length === 0 ? 'empty' : 'not-empty')}
        dataTestId={dataTestId}
      />
    </div>
  );
};

export const NO_LABEL_FILTER_VALUE = 'NO_LABEL';

export default LabelsTag;
